/**
 * Thermal printer service — WebUSB + WebBluetooth transport for ESC/POS printers.
 *
 * Responsibilities:
 *   - Discover and pair a printer (requestDevice with a user gesture).
 *   - Persist printer metadata (connection type, vendor/product IDs, paper width,
 *     auto-print, device display name) to Firestore at restaurants/{uid}.printerConfig.
 *   - Reconnect to a previously-paired device (browsers store USB/BT permission
 *     per-origin across sessions).
 *   - Send ESC/POS byte streams to the active device.
 *   - Format a receipt (via escpos.js) and print it.
 *
 * Important limitations (v1):
 *   - WebUSB and WebBluetooth are Chromium-only (Chrome / Edge / Opera). Safari,
 *     Firefox, iOS WebKit are NOT supported. `supportsWebUSB()` and
 *     `supportsWebBluetooth()` let callers detect this and fall back.
 *   - Pairing requires a user gesture (click). Silent background reconnect only
 *     works on devices the browser already holds permission for.
 *   - The Firestore config stores printer metadata, not the pairing itself. Each
 *     browser/machine grants USB permission independently (stored in the browser
 *     profile). If the same restaurant runs on two machines, both must pair.
 */

import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { buildReceipt, buildTestPrint } from './escpos';

// Generic serial-over-BLE service UUID used by most budget ESC/POS Bluetooth printers.
// Proprietary service; not GATT-standard, but the de-facto convention across
// Xprinter, MUNBYN, Rongta, and generic Chinese BT thermal modules.
const BT_SERIAL_SERVICE = '000018f0-0000-1000-8000-00805f9b34fb';
const BT_SERIAL_WRITE_CHAR = '00002af1-0000-1000-8000-00805f9b34fb';

// USB printer class
const USB_PRINTER_CLASS = 0x07;

// Keep the active connection in module scope so POS and ReceiptModal share it
// within a single browser session.
let activeDevice = null;
let activeTransport = null; // 'usb' | 'bluetooth'
let activeWriteCharacteristic = null; // BLE only
let activeOutEndpoint = null; // USB only

export function supportsWebUSB() {
  return typeof navigator !== 'undefined' && !!navigator.usb;
}

export function supportsWebBluetooth() {
  return typeof navigator !== 'undefined' && !!navigator.bluetooth;
}

/**
 * Load the persisted printer config for a restaurant.
 * Returns null if no config is set.
 */
export async function loadPrinterConfig(restaurantUid) {
  if (!restaurantUid) return null;
  const snap = await getDoc(doc(db, 'restaurants', restaurantUid));
  if (!snap.exists()) return null;
  return snap.data().printerConfig || null;
}

/**
 * Persist printer config. Merges into the restaurant doc.
 */
export async function savePrinterConfig(restaurantUid, config) {
  if (!restaurantUid) throw new Error('No restaurant id');
  const ref = doc(db, 'restaurants', restaurantUid);
  await updateDoc(ref, {
    printerConfig: {
      ...config,
      updatedAt: new Date().toISOString(),
    },
  });
}

/**
 * Remove printer config entirely.
 */
export async function clearPrinterConfig(restaurantUid) {
  if (!restaurantUid) throw new Error('No restaurant id');
  const ref = doc(db, 'restaurants', restaurantUid);
  await updateDoc(ref, { printerConfig: null });
  disconnect();
}

/**
 * Request a USB printer. Shows the browser's device picker — requires user gesture.
 * Returns device metadata suitable for saving to Firestore.
 */
export async function pairUsbPrinter() {
  if (!supportsWebUSB()) {
    throw new Error('WebUSB is not supported in this browser. Please use Chrome or Edge.');
  }
  const device = await navigator.usb.requestDevice({
    filters: [{ classCode: USB_PRINTER_CLASS }],
  });
  await openUsbDevice(device);
  return {
    connectionType: 'usb',
    vendorId: device.vendorId,
    productId: device.productId,
    deviceName: device.productName || device.manufacturerName || 'USB Thermal Printer',
    manufacturer: device.manufacturerName || null,
    serialNumber: device.serialNumber || null,
  };
}

/**
 * Request a Bluetooth printer. Shows the browser's device picker — requires user gesture.
 */
export async function pairBluetoothPrinter() {
  if (!supportsWebBluetooth()) {
    throw new Error('WebBluetooth is not supported in this browser. Please use Chrome or Edge on a device with Bluetooth.');
  }
  const device = await navigator.bluetooth.requestDevice({
    filters: [{ services: [BT_SERIAL_SERVICE] }],
    optionalServices: [BT_SERIAL_SERVICE],
  });
  await openBluetoothDevice(device);
  return {
    connectionType: 'bluetooth',
    deviceName: device.name || 'Bluetooth Thermal Printer',
    deviceId: device.id,
  };
}

/**
 * Open an already-granted USB device. Finds the first OUT bulk endpoint.
 */
async function openUsbDevice(device) {
  if (!device.opened) await device.open();
  if (device.configuration === null) await device.selectConfiguration(1);

  // Find an interface with a bulk OUT endpoint
  let interfaceNumber = -1;
  let outEndpoint = -1;
  const config = device.configuration;
  for (const iface of config.interfaces) {
    for (const alt of iface.alternates) {
      // Printer class or vendor-specific
      if (alt.interfaceClass === USB_PRINTER_CLASS || alt.interfaceClass === 0xff) {
        for (const ep of alt.endpoints) {
          if (ep.direction === 'out') {
            interfaceNumber = iface.interfaceNumber;
            outEndpoint = ep.endpointNumber;
            break;
          }
        }
      }
      if (outEndpoint >= 0) break;
    }
    if (outEndpoint >= 0) break;
  }

  if (outEndpoint < 0) {
    throw new Error('Could not find a printer endpoint on this USB device. Is this an ESC/POS thermal printer?');
  }

  await device.claimInterface(interfaceNumber);
  activeDevice = device;
  activeTransport = 'usb';
  activeOutEndpoint = outEndpoint;
}

async function openBluetoothDevice(device) {
  const server = await device.gatt.connect();
  const service = await server.getPrimaryService(BT_SERIAL_SERVICE);
  const characteristic = await service.getCharacteristic(BT_SERIAL_WRITE_CHAR);
  activeDevice = device;
  activeTransport = 'bluetooth';
  activeWriteCharacteristic = characteristic;
}

/**
 * Attempt to silently reconnect to a previously-paired device, using permissions
 * the browser already holds. Call this on app load / before printing.
 * Returns true on success, false if no granted device matches or reconnect fails.
 */
export async function tryReconnect(config) {
  if (!config) return false;
  try {
    if (config.connectionType === 'usb' && supportsWebUSB()) {
      const devices = await navigator.usb.getDevices();
      const match = devices.find(d =>
        d.vendorId === config.vendorId && d.productId === config.productId
      );
      if (!match) return false;
      await openUsbDevice(match);
      return true;
    }
    if (config.connectionType === 'bluetooth' && supportsWebBluetooth()) {
      // Bluetooth reconnect requires a user gesture in most browsers — this
      // usually fails silently outside of a click handler. Caller should retry
      // from a button press.
      if (!navigator.bluetooth.getDevices) return false;
      const devices = await navigator.bluetooth.getDevices();
      const match = devices.find(d => d.id === config.deviceId);
      if (!match) return false;
      await openBluetoothDevice(match);
      return true;
    }
  } catch (err) {
    console.warn('[printer] reconnect failed:', err.message);
    return false;
  }
  return false;
}

export function isConnected() {
  if (activeTransport === 'usb') return !!activeDevice && activeDevice.opened;
  if (activeTransport === 'bluetooth') return !!activeDevice && activeDevice.gatt?.connected;
  return false;
}

export function getActiveTransport() {
  return activeTransport;
}

export async function disconnect() {
  try {
    if (activeTransport === 'usb' && activeDevice) {
      await activeDevice.close();
    } else if (activeTransport === 'bluetooth' && activeDevice?.gatt?.connected) {
      activeDevice.gatt.disconnect();
    }
  } catch (err) {
    // Swallow — we're tearing down anyway.
    console.warn('[printer] disconnect error:', err.message);
  } finally {
    activeDevice = null;
    activeTransport = null;
    activeWriteCharacteristic = null;
    activeOutEndpoint = null;
  }
}

/**
 * Send raw ESC/POS bytes to the active printer. Caller must have paired/reconnected first.
 */
export async function sendBytes(bytes) {
  if (!isConnected()) {
    throw new Error('Printer is not connected. Set up the printer in Account > Printer.');
  }
  if (activeTransport === 'usb') {
    await activeDevice.transferOut(activeOutEndpoint, bytes);
    return;
  }
  if (activeTransport === 'bluetooth') {
    // BLE has a 512-byte MTU ceiling; chunk to 100 bytes for broad compatibility.
    const CHUNK = 100;
    for (let i = 0; i < bytes.length; i += CHUNK) {
      const chunk = bytes.slice(i, i + CHUNK);
      await activeWriteCharacteristic.writeValueWithoutResponse(chunk);
    }
    return;
  }
  throw new Error('No active printer transport.');
}

/**
 * High-level: print a full receipt using the configured paper width / auto-cut.
 * Tries a silent reconnect first if not connected.
 */
export async function printReceipt(receiptData, config) {
  if (!isConnected()) {
    const ok = await tryReconnect(config);
    if (!ok) {
      throw new Error('Printer is not connected. Open Account > Printer to reconnect.');
    }
  }
  const bytes = buildReceipt(receiptData, {
    paperWidth: config?.paperWidth || 80,
    autoCut: config?.autoCut !== false,
  });
  await sendBytes(bytes);
}

/**
 * Send a test receipt. Assumes the printer is already connected (setup wizard
 * pairs immediately before calling this).
 */
export async function printTestReceipt(restaurantName, paperWidth = 80) {
  if (!isConnected()) {
    throw new Error('Printer is not connected.');
  }
  const bytes = buildTestPrint(restaurantName, paperWidth);
  await sendBytes(bytes);
}
